import { useState, useEffect, useRef } from 'react';
import {
  Container, Row, Col, Card, CardBody, Button,
  Badge, Modal, ModalHeader, ModalBody, ModalFooter,
  Form, FormGroup, Label, Input, Alert,
} from 'reactstrap';
import { Plus, Edit2, Trash2, Users } from 'react-feather';
import { Breadcrumbs } from '../../AbstractElements';
import api from '../../api/prospect';
import { alertErro } from '../../utils/alerts';
import { useProspectAuth } from '../../Auth/ProspectAuthContext';

const ROLES = [{ id: 'vendedor', label: 'Vendedor' }, { id: 'gerente', label: 'Gerente' }];
const EMPTY_FORM = { name: '', phone: '', role: 'vendedor' };

function RadioGroup({ options, value, onChange }) {
  return (
    <div className='d-flex gap-2 flex-wrap'>
      {options.map(o => {
        const sel = value === o.id;
        return (
          <label key={o.id} onClick={() => onChange(o.id)} className='rounded' style={{
            cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '8px 14px',
            border: sel ? '2px solid #7366ff' : '1px solid #dee2e6',
            background: sel ? '#f0eeff' : '#fff', color: '#212529',
          }}>
            <span style={{ width: 16, height: 16, borderRadius: '50%', flexShrink: 0, marginRight: 8,
              border: sel ? '2px solid #7366ff' : '2px solid #dee2e6',
              background: sel ? '#7366ff' : '#fff',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              {sel && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#fff' }} />}
            </span>
            <span className='small fw-medium'>{o.label}</span>
          </label>
        );
      })}
    </div>
  );
}

export default function TimeComercial() {
  const { user } = useProspectAuth();
  const isAdmin   = user?.role === 'admin';

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(false);
  const [form, setForm]       = useState(EMPTY_FORM);
  const [editId, setEditId]   = useState(null);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [deleteId, setDeleteId] = useState(null);

  async function fetchMembers() {
    try {
      const { data } = await api.get('/team');
      setMembers(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  const memberNameRef = useRef();

  useEffect(() => { fetchMembers(); }, []);
  useEffect(() => { if (modal) setTimeout(() => memberNameRef.current?.focus(), 100); }, [modal]);

  function openNew() {
    setForm(EMPTY_FORM);
    setEditId(null);
    setError('');
    setModal(true);
  }

  function openEdit(m) {
    setForm({ name: m.name || '', phone: m.phone || '', role: m.role || 'vendedor' });
    setEditId(m.id);
    setError('');
    setModal(true);
  }

  async function handleSave() {
    if (!form.name.trim()) { setError('Nome é obrigatório.'); return; }
    if (!form.phone.trim()) { setError('WhatsApp é obrigatório.'); return; }
    setSaving(true);
    try {
      if (editId) {
        const { data } = await api.put(`/team/${editId}`, form);
        setMembers(prev => prev.map(m => m.id === editId ? data : m));
      } else {
        const { data } = await api.post('/team', form);
        setMembers(prev => [...prev, data]);
      }
      setModal(false);
    } catch (e) {
      setError(e.response?.data?.error || 'Não foi possível salvar o membro');
    } finally { setSaving(false); }
  }

  async function handleDelete() {
    try {
      await api.delete(`/team/${deleteId}`);
      setMembers(prev => prev.filter(m => m.id !== deleteId));
      setDeleteId(null);
    } catch (e) {
      alertErro('Erro ao excluir', e.response?.data?.error || 'Não foi possível excluir o membro');
    }
  }

  const f = (field, val) => setForm(p => ({ ...p, [field]: val }));

  return (
    <>
      <Breadcrumbs mainTitle='Time Comercial' parent='Topo Prospect' title='Time' />
      <Container fluid>

        {isAdmin && (
          <div className='alert alert-secondary d-flex align-items-center gap-2 mb-4' style={{ fontSize: '0.85rem' }}>
            <Users size={16} /> Modo auditoria — visualização apenas.
          </div>
        )}

        <div className='d-flex align-items-center justify-content-between mb-4'>
          <div>
            <h4 className='mb-0 fw-bold'>Time Comercial</h4>
            <p className='text-muted small mb-0'>Vendedores e gerentes que recebem notificações de leads</p>
          </div>
          {!isAdmin && (
            <Button color='primary' size='sm' onClick={openNew}>
              <Plus size={14} className='me-1' /> Novo membro
            </Button>
          )}
        </div>

        {loading ? (
          <p className='text-muted'>Carregando...</p>
        ) : members.length === 0 ? (
          <Card>
            <CardBody className='text-center py-5'>
              <Users size={40} className='text-muted mb-3' />
              <p className='text-muted'>Nenhum membro cadastrado.</p>
              {!isAdmin && (
                <Button color='primary' onClick={openNew}>
                  <Plus size={14} className='me-1' /> Adicionar primeiro membro
                </Button>
              )}
            </CardBody>
          </Card>
        ) : (
          <Row>
            {members.map(m => (
              <Col md={4} key={m.id} className='mb-4'>
                <Card className='h-100'>
                  <CardBody>
                    <div className='d-flex align-items-start justify-content-between mb-3'>
                      <div className='d-flex align-items-center gap-3'>
                        <div style={{ width: 44, height: 44, borderRadius: '50%',
                          background: m.role === 'gerente' ? '#f59e0b' : '#22c55e',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#fff', fontWeight: 700, fontSize: '1.1rem', flexShrink: 0 }}>
                          {m.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className='mb-0 fw-semibold'>{m.name}</p>
                          <p className='mb-0 small font-monospace text-muted'>{m.phone}</p>
                        </div>
                      </div>
                      <Badge color={m.role === 'gerente' ? 'warning' : 'success'}>
                        {m.role === 'gerente' ? 'Gerente' : 'Vendedor'}
                      </Badge>
                    </div>
                    {!isAdmin && (
                      <div className='d-flex gap-2 mt-3 pt-3 border-top'>
                        <Button color='light' size='sm' onClick={() => openEdit(m)}>
                          <Edit2 size={13} className='me-1' /> Editar
                        </Button>
                        <Button color='light' size='sm' className='text-danger' onClick={() => setDeleteId(m.id)}>
                          <Trash2 size={13} className='me-1' /> Excluir
                        </Button>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        )}

      </Container>

      {/* Modal */}
      <Modal isOpen={modal} toggle={() => setModal(false)}>
        <ModalHeader toggle={() => setModal(false)}>
          {editId ? 'Editar membro' : 'Novo membro'}
        </ModalHeader>
        <ModalBody>
          {error && <Alert color='danger' className='small'>{error}</Alert>}
          <Form>
            <FormGroup>
              <Label className='fw-medium small'>Nome *</Label>
              <Input innerRef={memberNameRef} value={form.name} onChange={e => f('name', e.target.value)} placeholder='Nome completo' />
            </FormGroup>
            <FormGroup>
              <Label className='fw-medium small'>WhatsApp * <span className='text-muted fw-normal'>(55DDD999999999)</span></Label>
              <Input
                value={form.phone}
                onChange={e => f('phone', e.target.value.replace(/\D/g, ''))}
                placeholder='5563999990000'
                maxLength={15}
              />
              <p className='text-muted mt-1 mb-0' style={{ fontSize: '0.75rem' }}>
                Inclua o código do país (55) + DDD + número
              </p>
            </FormGroup>
            <FormGroup>
              <Label className='fw-medium small'>Cargo</Label>
              <RadioGroup options={ROLES} value={form.role} onChange={v => f('role', v)} />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color='light' onClick={() => setModal(false)}>Cancelar</Button>
          <Button color='primary' disabled={saving} onClick={handleSave}>
            {saving ? 'Salvando...' : 'Salvar membro'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Confirmação exclusão */}
      <Modal isOpen={!!deleteId} toggle={() => setDeleteId(null)}>
        <ModalHeader toggle={() => setDeleteId(null)}>Remover membro?</ModalHeader>
        <ModalBody>
          <p className='text-muted small'>Esta ação não pode ser desfeita.</p>
        </ModalBody>
        <ModalFooter>
          <Button color='light' onClick={() => setDeleteId(null)}>Cancelar</Button>
          <Button color='danger' onClick={handleDelete}>Remover</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
